import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Plus, Building2, HelpCircle, User, ChevronDown, ChevronRight } from 'lucide-react';
import './OrgCanvas.css';

/* ───────────── Generic org-chart canvas ─────────────
 *
 * A reusable top-down hierarchy renderer shared by the Account Map (account
 * parent/child) and the Org Chart (contact reporting lines). The caller builds
 * a flat `nodes` array; this component handles tidy-tree auto-layout (a forest
 * of any number of roots), free drag-to-reposition, parent→child connectors,
 * and a connect handle for (re)parenting.
 *
 * Node shape:
 *   { key, type, id, name, sublabel, parentKey, savedX, savedY,
 *     isCurrent?, isRoot?, canBeParent?, raw? }
 *   - parentKey: key of the parent node, or null/missing for a root.
 *   - savedX/savedY: free position (null → auto layout slot).
 *   - canBeParent: false hides the connect handle (e.g. account placeholders).
 *   - type drives the icon + card style ('placeholder' | 'contact' | other).
 */
export const NODE_W = 210;
export const NODE_H = 78;
export const H_GAP = 28;       // horizontal gap between sibling subtrees
export const V_GAP = 54;       // vertical gap between levels
export const PADDING = 32;
const DRAG_THRESHOLD_PX = 4;

function pointFromEvent(e) {
  if (e.touches && e.touches.length) {
    return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
  }
  if (e.changedTouches && e.changedTouches.length) {
    return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
  }
  return { clientX: e.clientX, clientY: e.clientY };
}

// Vertical org-chart connector from parent bottom-center to child top-center.
export function connectorPath(parentPos, childPos) {
  const px = parentPos.x + NODE_W / 2;
  const py = parentPos.y + NODE_H;
  const cx = childPos.x + NODE_W / 2;
  const cy = childPos.y;
  const midY = (py + cy) / 2;
  return `M${px} ${py} C${px} ${midY} ${cx} ${midY} ${cx} ${cy}`;
}

// ─── Pure layout ────────────────────────────────────────────────────────────
// Tidy-tree "leaf counter": leaves take sequential x slots, parents center over
// their children, depth drives y. Handles a forest (multiple roots) laid out
// left-to-right. Saved positions override the auto slot. Returns nodes, byKey,
// resolved positions, and canvas dimensions — shared by the live canvas (which
// layers a drag override on top) and any PNG export.
export function layoutNodes(nodes) {
  const list = nodes || [];
  const byKey = new Map(list.map(n => [n.key, n]));
  const hasParent = (n) => n.parentKey && byKey.has(n.parentKey);

  const childrenOf = new Map();
  list.forEach(n => {
    if (hasParent(n)) {
      if (!childrenOf.has(n.parentKey)) childrenOf.set(n.parentKey, []);
      childrenOf.get(n.parentKey).push(n.key);
    }
  });
  const childSort = (ka, kb) => {
    const a = byKey.get(ka); const b = byKey.get(kb);
    if (a.type !== b.type) return a.type === 'placeholder' ? 1 : -1;
    return (a.name || '').localeCompare(b.name || '');
  };
  childrenOf.forEach(l => l.sort(childSort));

  // Collapse: every descendant of a node flagged `collapsed` is hidden (not
  // laid out, not rendered) so the subtree folds away.
  const hidden = new Set();
  const markHidden = (key) => {
    (childrenOf.get(key) || []).forEach(k => {
      if (!hidden.has(k)) { hidden.add(k); markHidden(k); }
    });
  };
  list.forEach(n => { if (n.collapsed) markHidden(n.key); });
  const visibleKids = (key) => (childrenOf.get(key) || []).filter(k => !hidden.has(k));

  const auto = new Map();
  let nextLeaf = 0;
  const visited = new Set();
  const walk = (key, depth) => {
    if (visited.has(key)) return; // defensive against malformed cycles
    visited.add(key);
    const y = PADDING + depth * (NODE_H + V_GAP);
    const kids = visibleKids(key); // empty when this node is collapsed
    if (kids.length === 0) {
      auto.set(key, { x: PADDING + nextLeaf * (NODE_W + H_GAP), y });
      nextLeaf++;
    } else {
      kids.forEach(k => walk(k, depth + 1));
      const first = auto.get(kids[0]);
      const last = auto.get(kids[kids.length - 1]);
      auto.set(key, { x: (first.x + last.x) / 2, y });
    }
  };
  // Roots first (explicit isRoot wins, then by name), laid out left-to-right.
  list
    .filter(n => !hasParent(n) && !hidden.has(n.key))
    .sort((a, b) => {
      if (!!a.isRoot !== !!b.isRoot) return a.isRoot ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    })
    .forEach(n => walk(n.key, 0));
  // Park any visible-but-unplaced node (cycle). Hidden nodes are left out.
  list.forEach(n => {
    if (!hidden.has(n.key) && !auto.has(n.key)) {
      auto.set(n.key, { x: PADDING + nextLeaf * (NODE_W + H_GAP), y: PADDING });
      nextLeaf++;
    }
  });

  const positions = new Map();
  list.forEach(n => {
    if (hidden.has(n.key)) return; // collapsed descendants get no position
    positions.set(
      n.key,
      (n.savedX != null && n.savedY != null) ? { x: n.savedX, y: n.savedY } : auto.get(n.key)
    );
  });
  let width = 600; let height = 360;
  positions.forEach(p => {
    width = Math.max(width, p.x + NODE_W + PADDING);
    height = Math.max(height, p.y + NODE_H + PADDING);
  });
  return { nodes: list, byKey, positions, width, height, childrenOf };
}

function nodeIcon(type) {
  if (type === 'placeholder') return <HelpCircle size={15} />;
  if (type === 'contact') return <User size={15} />;
  return <Building2 size={15} />;
}

function OrgCanvas({
  nodes,
  canEdit,
  onMoveNode,     // (node, x, y) — single-node move
  onMoveNodes,    // (moves:[{node,x,y}]) — used when dragMovesSubtree drags a whole subtree
  onNodeClick,    // (node)
  onConnect,      // (parentNode, childNode)
  onToggleCollapse, // (node) — when provided, nodes with children show a collapse toggle
  dragMovesSubtree = false, // drag a node → its whole subtree moves by the same delta
  connectTitle = 'Draw to another node to make it report to this one',
  emptyMessage = 'Nothing to show yet.',
  zoom = 1, // CSS zoom on the canvas; pointer math divides by it
}) {
  const canvasRef = useRef(null);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const gestureWasDragRef = useRef(false);
  const [dragInfo, setDragInfo] = useState(null);
  const [connectFrom, setConnectFrom] = useState(null); // nodeKey | null
  const [connectMouse, setConnectMouse] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);

  const base = useMemo(() => layoutNodes(nodes), [nodes]);
  const { nodes: list, byKey } = base;

  // All descendants (the whole subtree) of a node — includes collapsed/hidden
  // ones, so a subtree drag repositions out-of-view subordinates too.
  const descendantsOf = useCallback((key) => {
    const out = [];
    const seen = new Set();
    const queue = [...(base.childrenOf.get(key) || [])];
    while (queue.length) {
      const k = queue.shift();
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(k);
      (base.childrenOf.get(k) || []).forEach(c => queue.push(c));
    }
    return out;
  }, [base]);

  const positions = useMemo(() => {
    if (!dragInfo?.currentPos || !dragInfo.hasMoved) return base.positions;
    const dx = dragInfo.currentPos.x - dragInfo.startPos.x;
    const dy = dragInfo.currentPos.y - dragInfo.startPos.y;
    const pos = new Map(base.positions);
    pos.set(dragInfo.nodeKey, dragInfo.currentPos);
    if (dragMovesSubtree) {
      // Shift every visible descendant by the same delta during the drag.
      descendantsOf(dragInfo.nodeKey).forEach(k => {
        const b = base.positions.get(k);
        if (b) pos.set(k, { x: b.x + dx, y: b.y + dy });
      });
    }
    return pos;
  }, [base, dragInfo, dragMovesSubtree, descendantsOf]);

  const dims = { width: base.width, height: base.height };

  // ── Drag ───────────────────────────────────────────────────────────────
  const beginDrag = useCallback((clientX, clientY, node) => {
    if (!canEdit || connectFrom) return;
    gestureWasDragRef.current = false;
    const rect = canvasRef.current.getBoundingClientRect();
    const startMouse = { x: (clientX - rect.left) / zoomRef.current, y: (clientY - rect.top) / zoomRef.current };
    const startPos = positions.get(node.key);
    setDragInfo({ nodeKey: node.key, startMouse, startPos, currentPos: startPos, hasMoved: false });
  }, [canEdit, connectFrom, positions]);

  useEffect(() => {
    if (!dragInfo && !connectFrom) return;
    const handleMove = (e) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pt = pointFromEvent(e);
      const cx = (pt.clientX - rect.left) / zoomRef.current;
      const cy = (pt.clientY - rect.top) / zoomRef.current;
      if (dragInfo && e.cancelable && e.touches) e.preventDefault();
      if (dragInfo) {
        const dx = cx - dragInfo.startMouse.x;
        const dy = cy - dragInfo.startMouse.y;
        const hasMoved = dragInfo.hasMoved || Math.hypot(dx, dy) > DRAG_THRESHOLD_PX;
        if (hasMoved) gestureWasDragRef.current = true;
        setDragInfo({
          ...dragInfo,
          currentPos: {
            x: Math.max(0, dragInfo.startPos.x + dx),
            y: Math.max(0, dragInfo.startPos.y + dy),
          },
          hasMoved,
        });
      } else if (connectFrom) {
        setConnectMouse({ x: cx, y: cy });
      }
    };
    const handleUp = () => {
      if (dragInfo) {
        const finished = dragInfo;
        setDragInfo(null);
        if (finished.hasMoved) {
          const node = byKey.get(finished.nodeKey);
          if (!node) return;
          const nx = Math.round(finished.currentPos.x);
          const ny = Math.round(finished.currentPos.y);
          if (dragMovesSubtree && onMoveNodes) {
            // Persist the dragged node + every descendant, each shifted by the
            // same delta — so the subtree keeps its arrangement and follows the
            // manager from now on (including currently-collapsed subs).
            const dx = finished.currentPos.x - finished.startPos.x;
            const dy = finished.currentPos.y - finished.startPos.y;
            const moves = [{ node, x: nx, y: ny }];
            descendantsOf(finished.nodeKey).forEach(k => {
              const desc = byKey.get(k);
              const b = base.positions.get(k)
                || ((desc.savedX != null && desc.savedY != null) ? { x: desc.savedX, y: desc.savedY } : null);
              if (b) moves.push({ node: desc, x: Math.round(b.x + dx), y: Math.round(b.y + dy) });
            });
            onMoveNodes(moves);
          } else {
            onMoveNode(node, nx, ny);
          }
        }
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') { setDragInfo(null); setConnectFrom(null); setConnectMouse(null); }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleUp);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleUp);
      document.removeEventListener('keydown', handleKey);
    };
  }, [dragInfo, connectFrom, byKey, onMoveNode, onMoveNodes, dragMovesSubtree, base, descendantsOf]);

  // ── Connect (set parent) ─────────────────────────────────────────────────
  const startConnect = useCallback((node) => {
    if (!canEdit || node.canBeParent === false) return;
    setConnectFrom(node.key);
    setConnectMouse(null);
  }, [canEdit]);

  const resolveConnect = useCallback((targetNode) => {
    if (!connectFrom) return false;
    const parent = byKey.get(connectFrom);
    setConnectFrom(null);
    setConnectMouse(null);
    if (!parent || parent.key === targetNode.key) return true;
    onConnect(parent, targetNode);
    return true;
  }, [connectFrom, byKey, onConnect]);

  const handleCanvasMouseDown = useCallback((e) => {
    if (e.target !== canvasRef.current) return;
    if (connectFrom) { setConnectFrom(null); setConnectMouse(null); }
  }, [connectFrom]);

  // ── Render ───────────────────────────────────────────────────────────────
  const renderConnector = (node) => {
    if (!node.parentKey || !byKey.has(node.parentKey)) return null;
    const pPos = positions.get(node.parentKey);
    const cPos = positions.get(node.key);
    if (!pPos || !cPos) return null;
    const dimmed = connectFrom && connectFrom !== node.parentKey;
    return (
      <path key={`edge-${node.key}`} d={connectorPath(pPos, cPos)} className="am-edge" opacity={dimmed ? 0.3 : 1} fill="none" />
    );
  };

  const renderConnectPreview = () => {
    if (!connectFrom || !connectMouse) return null;
    const sp = positions.get(connectFrom);
    if (!sp) return null;
    const sx = sp.x + NODE_W / 2;
    const sy = sp.y + NODE_H;
    return (
      <path d={`M${sx} ${sy} L${connectMouse.x} ${connectMouse.y}`}
        stroke="#2563eb" strokeWidth="2" strokeDasharray="6 4" fill="none" style={{ pointerEvents: 'none' }} />
    );
  };

  return (
    <div
      ref={canvasRef}
      className={`am-canvas${connectFrom ? ' am-canvas-connecting' : ''}`}
      style={{ position: 'relative', width: dims.width, height: dims.height, minWidth: '100%', zoom }}
      onMouseDown={handleCanvasMouseDown}
    >
      <svg
        className="am-edge-layer"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}
      >
        {list.map(renderConnector)}
        {renderConnectPreview()}
      </svg>

      {list.map(node => {
        const pos = positions.get(node.key);
        if (!pos) return null;
        const isDragging = dragInfo?.nodeKey === node.key && dragInfo.hasMoved;
        const isConnectSource = connectFrom === node.key;
        const isConnectTarget = connectFrom && connectFrom !== node.key;
        const isHover = hoverKey === node.key;
        const childCount = (base.childrenOf.get(node.key) || []).length;
        const showCollapse = !!onToggleCollapse && childCount > 0;
        return (
          <div
            key={node.key}
            className={[
              'am-node',
              node.type === 'placeholder' ? 'am-node-placeholder' : 'am-node-account',
              node.isCurrent ? 'am-node-current' : '',
              node.isRoot ? 'am-node-root' : '',
              isDragging ? 'am-node-dragging' : '',
              isConnectSource ? 'am-connect-source' : '',
              isConnectTarget ? 'am-connect-target' : '',
            ].filter(Boolean).join(' ')}
            style={{
              left: pos.x, top: pos.y, width: NODE_W, height: NODE_H,
              cursor: connectFrom ? (isConnectSource ? 'pointer' : 'crosshair') : (canEdit ? 'grab' : 'pointer'),
              zIndex: isDragging ? 50 : 3,
            }}
            onMouseDown={(e) => { if (e.button === 0 && canEdit && !connectFrom) { e.preventDefault(); beginDrag(e.clientX, e.clientY, node); } }}
            onTouchStart={(e) => { if (e.touches?.length === 1 && canEdit && !connectFrom) beginDrag(e.touches[0].clientX, e.touches[0].clientY, node); }}
            onClick={() => {
              if (gestureWasDragRef.current) { gestureWasDragRef.current = false; return; }
              if (connectFrom) { resolveConnect(node); return; }
              onNodeClick && onNodeClick(node);
            }}
            onMouseEnter={() => setHoverKey(node.key)}
            onMouseLeave={() => setHoverKey(null)}
            role="button"
            tabIndex={0}
          >
            <div className="am-node-icon">{nodeIcon(node.type)}</div>
            <div className="am-node-body">
              <div className="am-node-name" title={node.name}>{node.name}</div>
              {node.sublabel && <div className="am-node-sub" title={node.sublabel}>{node.sublabel}</div>}
            </div>
            {node.isCurrent && <span className="am-node-badge">Viewing</span>}

            {/* Subordinate count + collapse/expand toggle (only when the
                consumer supports collapsing and this node has children). */}
            {showCollapse && (
              <button
                type="button"
                className={`am-node-collapse${node.collapsed ? ' collapsed' : ''}`}
                title={node.collapsed ? `Show ${childCount} report${childCount === 1 ? '' : 's'}` : `Hide ${childCount} report${childCount === 1 ? '' : 's'}`}
                aria-label={node.collapsed ? 'Expand reports' : 'Collapse reports'}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(node); }}
              >
                {node.collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                <span className="am-node-collapse-count">{childCount}</span>
              </button>
            )}

            {canEdit && node.canBeParent !== false && isHover && !connectFrom && !isDragging && (
              <button
                type="button"
                className="am-connect-handle"
                title={connectTitle}
                aria-label="Add child connection"
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); startConnect(node); }}
              >
                <Plus size={11} />
              </button>
            )}
          </div>
        );
      })}

      {list.length === 0 && (
        <div className="am-canvas-empty">{emptyMessage}</div>
      )}
    </div>
  );
}

export default OrgCanvas;
